import React, { useEffect, useState } from 'react';
import { campaignService } from '../../services/campaignService';
import ErrorMessage from '../UI/ErrorMessage';

interface TelnyxAssistantOption {
  id: string;
  name?: string;
  model?: string;
}

interface TelnyxAssistantPickerProps {
  value: string;
  onChange: (id: string) => void;
  error?: string | undefined;
  inputId?: string;
}

const TelnyxAssistantPicker: React.FC<TelnyxAssistantPickerProps> = ({
  value,
  onChange,
  error,
  inputId = 'telnyxAssistantId',
}) => {
  const [assistants, setAssistants] = useState<TelnyxAssistantOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    campaignService
      .listTelnyxAssistants()
      .then((list) => {
        if (!cancelled) setAssistants(list);
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : 'Failed to load assistants');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-2">
      <label htmlFor={inputId} className="block text-sm font-medium text-foreground">
        Telnyx Assistant *
      </label>
      {loading && <p className="text-xs text-muted-foreground">Loading assistants from Telnyx…</p>}
      {loadError && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          Could not load assistant list — enter ID manually below.
        </p>
      )}
      {assistants.length > 0 && (
        <select
          className="input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-label="Select Telnyx assistant"
        >
          <option value="">Select assistant…</option>
          {assistants.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name || a.id}
              {a.model ? ` (${a.model})` : ''}
            </option>
          ))}
        </select>
      )}
      <input
        id={inputId}
        type="text"
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="assistant-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
      />
      <ErrorMessage message={error} />
      <p className="text-xs text-muted-foreground">
        Pick from Telnyx or paste ID. Per-call script and greeting are sent via API override.
      </p>
    </div>
  );
};

export default TelnyxAssistantPicker;
